import _objectDestructuringEmpty from "@babel/runtime/helpers/esm/objectDestructuringEmpty";
import Raven from 'raven-js';
import { createContext, useContext, useEffect, useRef } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
const getBaseMarkName = (editorType, timerDependencies) => `editor-render${timerDependencies !== null && timerDependencies !== void 0 && timerDependencies.length ? `-${timerDependencies.join('-')}` : ''}-${editorType}`;
const getDurationMarkName = (editorType, timerDependencies) => `${getBaseMarkName(editorType, timerDependencies)}-duration`;
const getStartMarkName = (editorType, timerDependencies) => `${getBaseMarkName(editorType, timerDependencies)}-start-time`;
const markAndMeasureRenderDuration = (editorType, timerDependencies) => {
  try {
    const durationMarkName = getDurationMarkName(editorType, timerDependencies);
    const startMarkName = getStartMarkName(editorType, timerDependencies);
    if (performance.getEntriesByName(durationMarkName).length > 0) {
      return null;
    }
    const measurement = performance.measure(durationMarkName, startMarkName);
    performance.clearMarks(startMarkName);
    return measurement.duration;
  } catch (e) {
    // if performance is not compatible with the browser, ignore marks
    return null;
  }
};
const RichTextEditorMetricsContext = /*#__PURE__*/createContext(null);

/**
 * @param editorType The type of editor for which metrics are being measured  - this is fixed on first render and will not be updated if changed.
 * @param timerDependencies A list of dependencies that will be used to create a unique timer name and will reset the timer when they change.
 */
export const RichTextEditorMetricsProvider = ({
  children,
  editorType,
  timerDependencies = []
}) => {
  const contextValueRef = useRef({
    editorType,
    markAndMeasureRenderDuration: () => markAndMeasureRenderDuration(editorType, timerDependencies)
  });
  useEffect(() => {
    const startMarkName = getStartMarkName(contextValueRef.current.editorType, timerDependencies);
    contextValueRef.current.markAndMeasureRenderDuration = () => markAndMeasureRenderDuration(editorType, timerDependencies);
    try {
      performance.mark(startMarkName);
      return () => {
        performance.clearMarks(startMarkName);
      };
    } catch (e) {
      // if performance is not compatible with the browser, ignore marks
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, timerDependencies);
  return /*#__PURE__*/_jsx(RichTextEditorMetricsContext.Provider, {
    value: contextValueRef.current,
    children: children
  });
};
RichTextEditorMetricsProvider.displayName = 'RichTextEditorMetricsProvider';
export const withRichTextEditorMetrics = (editorType, WrappedComponent, timerDependenciesNames) => {
  const WithRichTextEditorMetrics = _ref => {
    let props = Object.assign({}, (_objectDestructuringEmpty(_ref), _ref));
    return /*#__PURE__*/_jsx(RichTextEditorMetricsProvider, {
      editorType: editorType,
      timerDependencies: Object.entries(props).filter(([key]) => timerDependenciesNames === null || timerDependenciesNames === void 0 ? void 0 : timerDependenciesNames.includes(key)).map(([__, value]) => {
        try {
          return JSON.stringify(value);
        } catch (e) {
          return `${value}`;
        }
      }),
      children: /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, props))
    });
  };
  WithRichTextEditorMetrics.displayName = `WithRichTextEditorMetrics(${WrappedComponent.displayName || 'UnknownComponent'})`;
  WithRichTextEditorMetrics._WrappedComponent = WrappedComponent;
  return WithRichTextEditorMetrics;
};
export const useRichTextEditorMetrics = () => {
  const context = useContext(RichTextEditorMetricsContext);
  if (!context) {
    return {
      editorType: 'unknown',
      markAndMeasureRenderDuration: () => {
        Raven.captureException(new Error('markAndMeasureRenderDuration called without context'));
        return null;
      }
    };
  }
  return context;
};